import { useContentStore } from "../../stores/content";
import {
  ComponentObject,
  CrossLayerConnection,
} from "../typeDefinations";
import ModuleTemplate from "../modules/moduleTemplate";
import {
  PayloadChannel,
  Point,
  PayloadCircle,
  PayloadShape,
  PayloadChannelWithHill
} from "@/library/payloadPrimitives";
import { Channel } from "@/library/channel";
import { LevelConnection } from "@/library/levelConnection";
import { InletOutlet } from "@/library/inletOutlet";
import { Valve } from "@/library/valve";
import { Bridge } from "@/library/bridge";
import ComponentPolygon from "@/library/componentPolygon";
import ComponentCircle from "@/library/componentCircle";
import { ChannelPoint} from "../other";
import { buildChannelPayload } from "./channelPayloadBuilder";
import { buildPayloadPolygonPrimitive } from "../primitives";
import StlWorker from 'worker-loader!@/library/utilities/stlWorker.worker';

const buildWall = (layerName: string, wallHeight?: number) => {
  const contentStore = useContentStore();
  const height = wallHeight ? wallHeight : contentStore.wallHeight;
  const channels = Array<PayloadChannelWithHill>();
  const channelPoints = [
    [[0, contentStore.wallThickness / 2], [contentStore.chipLengthX, contentStore.wallThickness / 2]],
    [[contentStore.chipLengthX - contentStore.wallThickness / 2, contentStore.wallThickness / 2], [contentStore.chipLengthX - contentStore.wallThickness / 2, contentStore.chipLengthY]],
    [[contentStore.chipLengthX, contentStore.chipLengthY - contentStore.wallThickness / 2], [0, contentStore.chipLengthY - contentStore.wallThickness / 2]],
    [[contentStore.wallThickness / 2, contentStore.chipLengthY - contentStore.wallThickness / 2], [contentStore.wallThickness / 2, contentStore.wallThickness / 2]]
  ];
  for (let i = 0; i < 4; i++) {
    const channel = new Channel(contentStore.wallThickness, height, layerName, 0, undefined, true);
    channel.channelDef.push(new ChannelPoint(channelPoints[i][0]));
    channel.channelDef.push(new ChannelPoint(channelPoints[i][1]));
    const wall = buildChannelPayload(channel, contentStore.chipThickness + height / 2, height);
    channels.push(wall);
  }
  return channels;
}

const createCornerComponent = (cornerId: string, points: number[][], circleCenter: Point, wallHeight?: number): ComponentObject[] => {
  const contentStore = useContentStore();
  const height = wallHeight ? wallHeight : contentStore.wallHeight;
  const compPolygon: ComponentObject = {
    id: `Polygon- ${cornerId}`,
    shapes: Array<PayloadShape>(),
    ports: Array<string>(),
    channels: Array<PayloadChannel>()
  };
  const shapes = buildPayloadPolygonPrimitive(
    points,
    false,
    0,
    contentStore.chipThickness,
    height,
    [0, 0],
    0,
    [0, 0]
  );
  if (shapes) compPolygon.shapes = shapes;

  const compCircle: ComponentObject = {
    id: `Circle-${cornerId}`,
    shapes: Array<PayloadShape>(),
    ports: Array<string>(),
    channels: Array<PayloadChannel>()
  };
  const circle = new PayloadCircle(
    circleCenter,
    false,
    height,
    contentStore.wallInnerCornerRadius
  );
  compCircle.shapes.push(circle);

  return [compPolygon, compCircle];
};

const createCorners = (wallHeight?: number) => {
  const contentStore = useContentStore();
  const corners = [
    {
      id: 'upperLeft',
      points: [
        [contentStore.wallThickness, contentStore.wallThickness], 
        [contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.wallThickness], 
        [contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.wallThickness + contentStore.wallInnerCornerRadius], 
        [contentStore.wallThickness, contentStore.wallThickness + contentStore.wallInnerCornerRadius]
      ],
      circleCenter: new Point(contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.chipThickness, contentStore.chipLengthY)
    },
    {
      id: 'upperRight',
      points: [
        [contentStore.chipLengthX - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.wallThickness], 
        [contentStore.chipLengthX - contentStore.wallThickness, contentStore.wallThickness], 
        [contentStore.chipLengthX - contentStore.wallThickness, contentStore.wallThickness + contentStore.wallInnerCornerRadius], 
        [contentStore.chipLengthX - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.wallThickness + contentStore.wallInnerCornerRadius]
      ],
      circleCenter: new Point(contentStore.chipLengthX - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.chipThickness, contentStore.chipLengthY)
    },
    {
      id: 'bottomRight',
      points: [
        [contentStore.chipLengthX - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.chipLengthY - contentStore.wallThickness - contentStore.wallInnerCornerRadius], 
        [contentStore.chipLengthX - contentStore.wallThickness, contentStore.chipLengthY - contentStore.wallThickness - contentStore.wallInnerCornerRadius], 
        [contentStore.chipLengthX - contentStore.wallThickness, contentStore.chipLengthY - contentStore.wallThickness], 
        [contentStore.chipLengthX - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.chipLengthY - contentStore.wallThickness]
      ],
      circleCenter: new Point(contentStore.chipLengthX - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.chipLengthY - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.chipThickness, contentStore.chipLengthY)
    },
    {
      id: 'bottomLeft',
      points: [
        [contentStore.wallThickness, contentStore.chipLengthY - contentStore.wallThickness - contentStore.wallInnerCornerRadius], 
        [contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.chipLengthY - contentStore.wallThickness - contentStore.wallInnerCornerRadius], 
        [contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.chipLengthY - contentStore.wallThickness], 
        [contentStore.wallThickness, contentStore.chipLengthY - contentStore.wallThickness]
      ],
      circleCenter: new Point(contentStore.wallThickness + contentStore.wallInnerCornerRadius, contentStore.chipLengthY - contentStore.wallThickness - contentStore.wallInnerCornerRadius, contentStore.chipThickness, contentStore.chipLengthY)
    }
  ];
  const comp = Array<ComponentObject>();
  if (wallHeight && wallHeight !== 0) {
    corners.forEach(corner => {
      const [polygon, circle] = createCornerComponent(corner.id, corner.points, corner.circleCenter, wallHeight);
      comp.push(polygon, circle);
    });
  } else if (contentStore.wallHeight !== 0) {
    corners.forEach(corner => {
      const [polygon, circle] = createCornerComponent(corner.id, corner.points, corner.circleCenter);
      comp.push(polygon, circle);
    });
  } return comp;
}

const checkLevelConnection: (elm: LevelConnection | InletOutlet, layer: string) => boolean = (elm, layer) => {
  if (elm instanceof LevelConnection) {
    const startIndex = parseInt(elm.properties.endLv.value.split(' ')[1]);
    const endIndex = parseInt(elm.properties.startLv.value.split(' ')[1]);
    const lowerIndex = Math.min(startIndex, endIndex);
    const higherIndex = Math.max(startIndex, endIndex);
    if (parseInt(layer.split(' ')[1]) >= lowerIndex && parseInt(layer.split(' ')[1]) < higherIndex) {
      return true;
    } else return false;
  } else {
    if (elm.layers.layers.map(layerObject => layerObject.layer).includes(layer)) {
      return true;
    } else return false;
  }
}

const checkForNegativeShape = (p: ComponentObject): boolean => {
  return p.shapes.length === 1 && !p.shapes[0].fill ? true : false;
}

// called for every layer, directly returns STLData instead of JSON-requestbody
export const buildSTLBuffer = (
  precision: string,
  binary: boolean,
  layer: { height: number; color: string; label: string; id: string; contentIds: string[]; },
  worker: StlWorker,
  wallHeight?: number
): Promise<ArrayBuffer> => {
  // set up store and arrays
  const contentStore = useContentStore();
  const wallComponents = Array<ComponentObject>();
  const components = Array<ComponentObject>();
  const channels = Array<PayloadChannelWithHill>();
  const crosslayerConnections: Array<CrossLayerConnection> = [];
  const valves = Array<ComponentObject>();
  const elevation = layer.height;
  let containsNegativeShape = false;
  // get chip properties
  const chipLengthX = contentStore.chipLengthX;
  const chipLengthY = contentStore.chipLengthY;
  const chipThickness = contentStore.chipThickness;
  // set ring resolution
  let ringResolution = 0;
  if (precision === "Low") {
    ringResolution = 16;
  } else if (precision === "Medium") {
    ringResolution = 24;
  } else {
    ringResolution = 40;
  }
  // build wall
  const layerName = layer.id;
  if (wallHeight && wallHeight !== 0) {
    const wallChannels = buildWall(layerName, wallHeight);
    channels.push(...wallChannels);
  } else if (contentStore.wallHeight !== 0 && contentStore.wallThickness !== 0) {
    const wallChannels = buildWall(layerName);
    channels.push(...wallChannels);
  }
  // wall inner corner radius
  if (contentStore.wallInnerCornerRadius !== 0) {
    const wallCorners = wallHeight ? createCorners(wallHeight): createCorners();
    wallCorners.forEach(corner => {
      wallComponents.push(corner);
    });
  }
  // reset channel points
  layer.contentIds.forEach((id) => {
    const elm = contentStore.getElement(id);
    if (elm instanceof Channel) {
      elm.resetBridges();
      elm.resetValves();
    }
  });
  // bind bridges and valves to channel points
  layer.contentIds.forEach((id) => {
    const elm = contentStore.getElement(id);
    if (elm.display && elm instanceof Bridge) {
      elm.channelSegment.start.bridges.push(elm);
    } else if (elm.display && elm instanceof Valve) {
      elm.channelSegment.start.valves.push(elm);
    }
  });
  // build payload
  layer.contentIds.forEach((id) => {
    const elm = contentStore.getElement(id);
    if (elm.display) {
      if (
          elm instanceof ModuleTemplate ||
          elm instanceof ComponentPolygon ||
          elm instanceof ComponentCircle
      ) {
        const p = elm.buildPayload(elevation, 0);
        if (p) {
          if ((elm instanceof ComponentPolygon || elm instanceof ComponentCircle) && checkForNegativeShape(p)) {
            containsNegativeShape = true;
          }
          components.push(p);
        }
      } else if (elm instanceof Channel) {
          channels.push(elm.buildPayload(elevation, 0));
      } else if (elm instanceof Valve) {
          valves.push(elm.buildPayload(elevation));
      }
    }
  });
  // build level connection
  contentStore.levelConnectionIds.forEach((id) => {
    const elm = contentStore.getElement(id);
    if (elm instanceof LevelConnection && elm.display && checkLevelConnection(elm, layer.id)) {
      const p = elm.buildPayload(() => {
        return 0;
      }, layer.id);

      if (p) {
        crosslayerConnections.push(p);
      }
    }
  });
  // build ports
  contentStore.portIds.forEach((id) => {
    const elm = contentStore.getElement(id);
    let p = null;
    if (elm instanceof InletOutlet && elm.display && checkLevelConnection(elm, layer.id)) {
      if (wallHeight && wallHeight !== 0) {
        p = elm.buildPayload(elevation, layerName, wallHeight);
      } else {
        p = elm.buildPayload(elevation, layerName);
      }
      if (p) {
        crosslayerConnections.push(p);
      }
    }
  });
  // clone arrays
  const clonedWallComponents = JSON.parse(JSON.stringify(wallComponents));
  const clonedComponents = JSON.parse(JSON.stringify(components));
  const clonedChannels = JSON.parse(JSON.stringify(channels));
  const clonedCrosslayerConnections = JSON.parse(JSON.stringify(crosslayerConnections));
  const clonedValves = JSON.parse(JSON.stringify(valves));
  // build chip
  return new Promise<ArrayBuffer>((resolve, reject) => {
    worker.postMessage({ clonedWallComponents, clonedComponents, clonedChannels, ringResolution, chipThickness, chipLengthX, chipLengthY, clonedCrosslayerConnections, clonedValves, binary, containsNegativeShape });

    worker.onmessage = (event: MessageEvent) => {
      const { success, buffer, error } = event.data;
      if (success) {
        resolve(buffer);
      } else {
        console.error(error);
        reject(new Error(error));
      }
      worker.terminate();
    };

    worker.onerror = (error: ErrorEvent) => {
      console.error('STL-Generation was not successful', error);
      reject(error);
      worker.terminate();
    };
  });
};
